<script lang="ts">
  import { GOLDEN_RATIO } from "@/libs/math"
  import { onMount, type Snippet } from "svelte"
  import type { HTMLAttributes } from "svelte/elements"

  let {
    frontLayer,
    faceBox,
    guidance,
    indicator,
    children,
    ...props
  }: {
    frontLayer?: Snippet
    faceBox?: Snippet
    guidance?: Snippet
    indicator?: Snippet
    children?: Snippet
  } & HTMLAttributes<HTMLDivElement> = $props()

  let layout = $state<HTMLElement>()
  let layoutHeight = $state(0)
  let box = $state<HTMLElement>()

  onMount(() => {
    if (!layout) return

    const AR = 240 / 317

    layoutHeight = layout.clientHeight

    const observer = new ResizeObserver(() => {
      if (!layout || !box) return

      layoutHeight = layout.clientHeight

      const viewport = {
        with: layout.clientWidth,
        height: layout.clientHeight,
      }

      let targetY = 0.33

      let face = {
        width: viewport.with * (282 / 390),
        height: viewport.height * (294 / 469),
      }

      // Workaround for LIQA-1230 issue
      // TODO: design a general solution
      if (isSmallViewport) {
        face = {
          width: viewport.with * (214 / 306),
          height: viewport.height * (280 / 360),
        }

        targetY = 0.3666
      }

      if (face.width / face.height > AR) face.width = face.height * AR
      else face.height = face.width / AR

      const eyesY = face.height * GOLDEN_RATIO
      const offset = targetY * viewport.height - eyesY

      Object.assign(box.style, {
        width: `${face.width}px`,
        height: `${face.height}px`,
        marginTop: `${offset}px`,
      })
    })

    observer.observe(layout)

    return () => observer.disconnect()
  })

  let isSmallViewport = $derived(layoutHeight < 400)
  let hasHelp = $derived(Boolean(guidance || indicator))
</script>

<div {...props} class:hud={["hud", { dimmed: hasHelp }, props.class]} bind:this={layout}>
  <div class="face-box" class:space-restricted={isSmallViewport} bind:this={box}>
    {@render faceBox?.()}
  </div>
  {#if frontLayer}
    <div class="absolute left-0 top-0 isolate z-10 size-full">
      {@render frontLayer()}
    </div>
  {/if}
  {#if hasHelp}
    <div class="help">
      <div class="guidance">{@render guidance?.()}</div>
      <div class="indicator">{@render indicator?.()}</div>
    </div>
  {/if}
  {@render children?.()}
</div>

<style lang="postcss">
  .hud {
    @apply relative flex h-full w-full flex-col items-center;

    &.dimmed {
      background: linear-gradient(
          180deg,
          rgba(0, 0, 0, 0) 0%,
          rgba(0, 0, 0, 0.01) 11.79%,
          rgba(0, 0, 0, 0.03) 21.38%,
          rgba(0, 0, 0, 0.06) 29.12%,
          rgba(0, 0, 0, 0.11) 35.34%,
          rgba(0, 0, 0, 0.16) 40.37%,
          rgba(0, 0, 0, 0.23) 44.56%,
          rgba(0, 0, 0, 0.3) 48.24%,
          rgba(0, 0, 0, 0.37) 51.76%,
          rgba(0, 0, 0, 0.45) 55.44%,
          rgba(0, 0, 0, 0.53) 59.63%,
          rgba(0, 0, 0, 0.61) 64.66%,
          rgba(0, 0, 0, 0.69) 70.88%,
          rgba(0, 0, 0, 0.76) 78.62%,
          rgba(0, 0, 0, 0.83) 88.21%,
          rgba(0, 0, 0, 0.9) 100%
        )
        center bottom / 100% calc(376 / 726 * 100%) no-repeat;
    }

    & > * {
      @apply min-h-0 flex-shrink-0;
    }
  }

  :global([data-ui-scale="sm"]) .hud.dimmed {
    background-size: 100% calc(199 / 469 * 100%);
  }
  :global([data-ui-scale="xs"]) .hud.dimmed {
    background-size: 100% calc(199 / 360 * 100%);
  }

  .face-box {
    @apply relative flex flex-col items-center justify-center;

    &.space-restricted {
      @apply transition-[height,margin-top];

      &:empty {
        @apply !mt-0 !h-[calc(100%-(48px+70px))];
      }
    }

    & > :global(*) {
      @apply absolute;
    }
  }

  .help {
    @apply flex w-full grow flex-col;
  }

  .guidance {
    @apply relative mt-2 flex min-h-[3.25rem] w-full flex-col items-center justify-center overflow-hidden whitespace-nowrap px-2 py-3 text-2xl/7;
    @apply text-center font-medium -tracking-[0.05em];
    text-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);

    &:empty {
      @apply hidden;
    }
  }

  :global([data-ui-scale="xs"]) .guidance,
  :global([data-ui-scale="sm"]) .guidance {
    @apply mt-0 min-h-[3rem] text-xl/6;
  }

  .indicator {
    @apply relative mt-6 flex w-full grow flex-col items-center justify-start;

    &:empty {
      @apply hidden;
    }

    & > :global(*) {
      @apply absolute;
    }
  }

  :global([data-ui-scale="sm"]) .indicator {
    @apply mt-3;
  }
  :global([data-ui-scale="xs"]) .indicator {
    @apply mt-2.5;
  }
</style>
